import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import axios from 'axios'
import { Dialog, DialogContent } from 'components/ui/dialog'
import {
	ONBOARDING_STEPS,
	initializeOnboarding,
	advanceOnboardingStep,
	skipOnboarding,
} from 'slices/onboardingSlice'
import OnboardingProgress from './steps/OnboardingProgress'
import ProfileStep from './steps/ProfileStep'
import InviteStep from './steps/InviteStep'
import { Button } from 'components/ui/button'
import { Users, Loader2 } from 'lucide-react'
import { withAuth } from 'utils/authHeaders'

/**
 * Invited users skip location/project creation since they already have
 * access to the inviter's shared assets via partner relationships.
 *
 * Steps: Welcome (custom) → Profile → Invite → Complete
 */

// Simplified step mapping for invited users
const INVITED_STEPS = {
	WELCOME: 0,
	PROFILE: 1,
	INVITE: 5,
	COMPLETE: 6,
}

const InvitedWelcomeStep: React.FC<{
	user: any
	inviterName: string | null
	onSkip: () => void
}> = ({ user, inviterName, onSkip }) => {
	const dispatch = useDispatch()
	const { saving } = useSelector((state: any) => state.onboarding)

	const handleNext = () => {
		dispatch(
			advanceOnboardingStep({
				nextStep: INVITED_STEPS.PROFILE,
				userId: user._id,
			}),
		)
	}

	return (
		<div className="p-6 flex flex-col items-center text-center">
			<div className="w-14 h-14 rounded-full bg-[#0080fe]/10 flex items-center justify-center mb-4">
				<Users className="w-7 h-7 text-[#0080fe]" />
			</div>

			<h2 className="text-2xl font-semibold mb-1">
				Welcome to{' '}
				<span className="text-[#052852]">MY</span>
				<span className="text-[#25a4fc]">Deal</span>
				<span className="text-[#074f9b]">Teams</span>!
			</h2>

			{inviterName && (
				<p className="text-gray-600 text-sm mb-2">
					You've been invited by <strong>{inviterName}</strong> to collaborate.
				</p>
			)}

			<p className="text-gray-500 text-sm mb-6">
				Let's complete your profile so your team can recognize you. This will only take a minute.
			</p>

			<div className="w-full space-y-3 mb-6 text-left">
				<div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50">
					<span className="text-green-500 mt-0.5">✓</span>
					<span className="text-sm text-gray-700">
						Your account has been created and linked to shared projects
					</span>
				</div>
				<div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50">
					<span className="text-[#0080fe] mt-0.5">→</span>
					<span className="text-sm text-gray-700">
						Next: Complete your profile so your team can recognize you
					</span>
				</div>
			</div>

			<div className="flex flex-col gap-2 w-full max-w-xs">
				<Button onClick={handleNext} isLarge disabled={saving} className="w-full">
					{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Complete My Profile'}
				</Button>
				<Button variant="ghost" onClick={onSkip} disabled={saving} className="w-full text-gray-500">
					Skip for Now
				</Button>
			</div>
		</div>
	)
}

const OnboardingFlowInvited: React.FC = () => {
	const dispatch = useDispatch()

	const userLogin = useSelector((state: any) => state.userLogin)
	const { userInfo: user } = userLogin

	const onboarding = useSelector((state: any) => state.onboarding)
	const { currentStep, isOnboarding } = onboarding

	const [inviterName, setInviterName] = useState<string | null>(null)

	useEffect(() => {
		if (user) {
			dispatch(
				initializeOnboarding({
					step: user.onboardingStep || 0,
					isFirstTime: user.firstTime,
				}),
			)

			// Try to fetch inviter name
			const fetchInviter = async () => {
				try {
					const res = await axios.get(
						`/api/invitedUsers/email?email=${user.email}`,
						withAuth(),
					)
					if (res.data?.inviterId) {
						const inviterRes = await axios.get(`/api/users/${res.data.inviterId}`)
						if (inviterRes.data?.name) {
							setInviterName(inviterRes.data.name)
						}
					}
				} catch {
					// Inviter lookup is best-effort
				}
			}
			fetchInviter()
		}
	}, [dispatch, user])

	if (!user || !isOnboarding) return null

	const handleSkipAll = () => {
		dispatch(skipOnboarding(user._id))
	}

	const handleClose = () => {
		// intentionally empty — user must use Skip or complete steps
	}

	const renderStep = () => {
		switch (currentStep) {
			case INVITED_STEPS.WELCOME:
				return (
					<InvitedWelcomeStep
						user={user}
						inviterName={inviterName}
						onSkip={handleSkipAll}
					/>
				)
			case INVITED_STEPS.PROFILE:
				return <ProfileStep user={user} onSkip={handleSkipAll} />
			case INVITED_STEPS.INVITE:
				return <InviteStep user={user} onSkip={handleSkipAll} />
			default:
				// For steps 2-4 (location/project/task), skip ahead to invite
				if (currentStep >= INVITED_STEPS.PROFILE && currentStep < INVITED_STEPS.INVITE) {
					dispatch(
						advanceOnboardingStep({
							nextStep: INVITED_STEPS.INVITE,
							userId: user._id,
						}),
					)
				}
				return null
		}
	}

	// Show simplified progress for invited flow (3 steps instead of 6)
	const invitedStepIndex =
		currentStep === INVITED_STEPS.WELCOME
			? 0
			: currentStep === INVITED_STEPS.PROFILE
				? 1
				: 2

	return (
		<Dialog open={true} onClose={handleClose}>
			<DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
				{currentStep > INVITED_STEPS.WELCOME && (
					<div className="border-b border-gray-100">
						<div className="flex items-center justify-center gap-2 py-3 px-4">
							{['Welcome', 'Profile', 'Invite'].map((label, i) => (
								<React.Fragment key={label}>
									<div className="flex flex-col items-center gap-1">
										<div
											className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
												i < invitedStepIndex
													? 'bg-[#0080fe] text-white'
													: i === invitedStepIndex
														? 'bg-[#0080fe]/10 text-[#0080fe] ring-2 ring-[#0080fe]'
														: 'bg-gray-100 text-gray-400'
											}`}
										>
											{i < invitedStepIndex ? '✓' : i + 1}
										</div>
										<span
											className={`text-[10px] font-medium ${
												i === invitedStepIndex ? 'text-[#0080fe]' : 'text-gray-400'
											}`}
										>
											{label}
										</span>
									</div>
									{i < 2 && (
										<div
											className={`flex-1 h-0.5 min-w-[20px] mt-[-14px] ${
												i < invitedStepIndex ? 'bg-[#0080fe]' : 'bg-gray-200'
											}`}
										/>
									)}
								</React.Fragment>
							))}
						</div>
					</div>
				)}
				{renderStep()}
			</DialogContent>
		</Dialog>
	)
}

export default OnboardingFlowInvited
